import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Button, Modal, Typography } from '@mui/material';

import { ROUTE_CONSTANTS } from '../app/RouteBuilding/routeConstants';

import modalStyles from './JobCreateTypeSelectModal.styles';

const JOB_CREATE_TYPES = {
  SET_SCHEDULE: 'set_schedule',
  FLEXIBLE_SCHEDULE: 'flexible_schedule',
  RECURRING_2_WEEK: 'recurring_2_week',
};

const JobCreateTypeSelectModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);

  const handleOptionSelect = (type) => {
    setSelectedType(type);
  };

  const handleNext = () => {
    if (selectedType === JOB_CREATE_TYPES.SET_SCHEDULE) {
      navigate(ROUTE_CONSTANTS.JOB_ORDER_CREATE);
    } else if (selectedType === JOB_CREATE_TYPES.FLEXIBLE_SCHEDULE) {
      navigate(ROUTE_CONSTANTS.JOB_ORDER_CREATE_BULK_DAY);
    } else if (selectedType === JOB_CREATE_TYPES.RECURRING_2_WEEK) {
      navigate(ROUTE_CONSTANTS.JOB_ORDER_CREATE_RECURRING);
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedType(null);
    onClose();
  };

  const renderExample = (type) => {
    if (type === JOB_CREATE_TYPES.SET_SCHEDULE) {
      return (
        <Box sx={modalStyles.exampleContainer}>
          <Box sx={modalStyles.examplePill}>
            {LanguageConverterV2('job.create.typeSelect.example.label')}
          </Box>
          <Box sx={modalStyles.exampleLines}>
            <Typography sx={modalStyles.exampleLine} component="div">
              <Box component="span" sx={modalStyles.exampleBold}>
                {LanguageConverterV2('job.create.typeSelect.setSchedule.example.line1.bold')}
              </Box>
              <Box component="span" sx={modalStyles.exampleItalic}>
                {LanguageConverterV2('job.create.typeSelect.setSchedule.example.line1.rest')}
              </Box>
            </Typography>
            <Typography sx={modalStyles.exampleLine} component="div">
              <Box component="span" sx={modalStyles.exampleItalic}>
                {LanguageConverterV2('job.create.typeSelect.setSchedule.example.line2')}
              </Box>
            </Typography>
          </Box>
        </Box>
      );
    }

    if (type === JOB_CREATE_TYPES.FLEXIBLE_SCHEDULE) {
      return (
        <Box sx={modalStyles.exampleContainer}>
          <Box sx={modalStyles.examplePill}>
            {LanguageConverterV2('job.create.typeSelect.example.label')}
          </Box>
          <Box sx={modalStyles.exampleLines}>
            <Typography sx={modalStyles.exampleLine} component="div">
              <Box component="span" sx={modalStyles.exampleBold}>
                {LanguageConverterV2('job.create.typeSelect.flexibleSchedule.example.line1.bold')}
              </Box>
              <Box component="span" sx={modalStyles.exampleItalic}>
                {LanguageConverterV2('job.create.typeSelect.flexibleSchedule.example.line1.rest')}
              </Box>
            </Typography>
            <Typography sx={modalStyles.exampleLine} component="div">
              <Box component="span" sx={modalStyles.exampleBold}>
                {LanguageConverterV2('job.create.typeSelect.flexibleSchedule.example.line2.bold')}
              </Box>
              <Box component="span" sx={modalStyles.exampleItalic}>
                {LanguageConverterV2('job.create.typeSelect.flexibleSchedule.example.line2.rest')}
              </Box>
            </Typography>
            <Typography sx={modalStyles.exampleLine} component="div">
              <Box component="span" sx={modalStyles.exampleBold}>
                {LanguageConverterV2('job.create.typeSelect.flexibleSchedule.example.line3.bold')}
              </Box>
              <Box component="span" sx={modalStyles.exampleItalic}>
                {LanguageConverterV2('job.create.typeSelect.flexibleSchedule.example.line3.rest')}
              </Box>
            </Typography>
          </Box>
        </Box>
      );
    }

    if (type === JOB_CREATE_TYPES.RECURRING_2_WEEK) {
      return (
        <Box sx={modalStyles.exampleContainer}>
          <Box sx={modalStyles.examplePill}>
            {LanguageConverterV2('job.create.typeSelect.example.label')}
          </Box>
          <Box sx={modalStyles.exampleLines}>
            <Typography sx={modalStyles.exampleLine} component="div">
              <Box component="span" sx={modalStyles.exampleWeekLabel}>
                {LanguageConverterV2('job.create.typeSelect.recurring2Week.example.week1.label')}
              </Box>
            </Typography>
            <Typography sx={modalStyles.exampleLine} component="div">
              <Box component="span" sx={modalStyles.exampleItalic}>
                {LanguageConverterV2('job.create.typeSelect.recurring2Week.example.week1.detail')}
              </Box>
            </Typography>

            <Typography sx={modalStyles.exampleLine} component="div">
              <Box component="span" sx={modalStyles.exampleWeekLabel}>
                {LanguageConverterV2('job.create.typeSelect.recurring2Week.example.week2.label')}
              </Box>
            </Typography>
            <Typography sx={modalStyles.exampleLine} component="div">
              <Box component="span" sx={modalStyles.exampleItalic}>
                {LanguageConverterV2('job.create.typeSelect.recurring2Week.example.week2.detail1')}
              </Box>
            </Typography>
            <Typography sx={modalStyles.exampleLine} component="div">
              <Box component="span" sx={modalStyles.exampleItalic}>
                {LanguageConverterV2('job.create.typeSelect.recurring2Week.example.week2.detail2')}
              </Box>
            </Typography>

            <Box sx={modalStyles.exampleRepeatRow}>
              <Box sx={modalStyles.exampleRepeatDot} />
              <Typography sx={modalStyles.exampleRepeatText} component="div">
                {LanguageConverterV2('job.create.typeSelect.recurring2Week.example.repeatNote')}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    return null;
  };

  const renderOptionCard = (type, titleKey, descriptionKey, bestForKey) => {
    const isSelected = selectedType === type;

    return (
      <Box
        key={type}
        sx={{
          ...modalStyles.optionCard,
          ...(isSelected && modalStyles.selectedOptionCard),
        }}
        onClick={() => handleOptionSelect(type)}
      >
        <Typography sx={modalStyles.optionTitle}>{LanguageConverterV2(titleKey)}</Typography>
        <Typography sx={modalStyles.optionDescription}>
          {LanguageConverterV2(descriptionKey)}
        </Typography>
        {renderExample(type)}
        <Box sx={modalStyles.optionBestForContainer}>
          <Typography sx={modalStyles.optionBestFor}>
            {LanguageConverterV2('job.create.typeSelect.bestFor.label')}
          </Typography>
          <Typography sx={modalStyles.optionBestForText}>
            {LanguageConverterV2(bestForKey)}
          </Typography>
        </Box>

        <Box sx={modalStyles.selectionIndicator}>
          {isSelected && (
            <Typography sx={modalStyles.selectedText}>
              {LanguageConverterV2('job.create.typeSelect.selected')}
            </Typography>
          )}
          <Box
            sx={{
              ...modalStyles.radioButton,
              ...(isSelected && modalStyles.selectedRadioButton),
            }}
          >
            {isSelected && <Box sx={modalStyles.radioInner} />}
          </Box>
        </Box>
      </Box>
    );
  };

  const options = [
    {
      type: JOB_CREATE_TYPES.SET_SCHEDULE,
      titleKey: 'job.create.typeSelect.setSchedule.title',
      descriptionKey: 'job.create.typeSelect.setSchedule.description',
      bestForKey: 'job.create.typeSelect.setSchedule.bestFor',
    },
    {
      type: JOB_CREATE_TYPES.FLEXIBLE_SCHEDULE,
      titleKey: 'job.create.typeSelect.flexibleSchedule.title',
      descriptionKey: 'job.create.typeSelect.flexibleSchedule.description',
      bestForKey: 'job.create.typeSelect.flexibleSchedule.bestFor',
    },
    {
      type: JOB_CREATE_TYPES.RECURRING_2_WEEK,
      titleKey: 'job.create.typeSelect.recurring2Week.title',
      descriptionKey: 'job.create.typeSelect.recurring2Week.description',
      bestForKey: 'job.create.typeSelect.recurring2Week.bestFor',
    },
  ];

  return (
    <Modal
      open={isOpen}
      onClose={handleCancel}
      aria-labelledby="job-create-type-modal-title"
      aria-describedby="job-create-type-modal-description"
      sx={modalStyles.container}
    >
      <Box sx={modalStyles.modalBox}>
        <Box sx={modalStyles.contentBox}>
          <Box>
            <Typography sx={modalStyles.title}>
              {LanguageConverterV2('job.create.typeSelect.title')}
            </Typography>
            <Typography sx={modalStyles.subtitle}>
              {LanguageConverterV2('job.create.typeSelect.subtitle')}
            </Typography>
          </Box>

          <Box sx={modalStyles.optionsContainer}>
            {options.map((option) =>
              renderOptionCard(
                option.type,
                option.titleKey,
                option.descriptionKey,
                option.bestForKey
              )
            )}
          </Box>

          <Box sx={modalStyles.buttonsContainer}>
            <Button variant="outlined" onClick={handleCancel} sx={modalStyles.cancelButton}>
              {LanguageConverterV2('button.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!selectedType}
              sx={modalStyles.nextButton}
            >
              {LanguageConverterV2('button.next')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

JobCreateTypeSelectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default JobCreateTypeSelectModal;
